import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Clip } from '@ff-os/rendering-shared/Clip';
import { Area } from '@ff-os/shared-utils/types/Area';
import { Size } from '@ff-os/shared-utils/types/Size';
import 'react-image-crop/dist/ReactCrop.css';
import classNames from 'classnames';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop/src/index';
import { CropOverlay } from './CropOverlay';
import styles from './CropTool.module.scss';

const MIN_CROP_BOX_SIZE_RATIO = 0.25;

export const CropTool = ({
    onChange, overlayUrl, filter,
    imageSize, activeCropBox,
    containerSize, clip, aspectRatio,
    children
}: Props) => {

    const [minimumSize, setMinimumSize] = useState(calculateMinimumSizeOf(imageSize.width, imageSize.height));
    const [animateControls, setAnimateControls] = useState(true);
    const [crop, setCrop] = useState<Crop>();

    // The crop box should scale to real client width and height. Otherwise the minimum size is larger than the rendered image.
    // Issue: https://github.com/sekoyo/react-image-crop/issues/446#issuecomment-1289244694
    const containerRef = useCallback(node => {
        if (node !== null) {
            const { width, height } = node.getBoundingClientRect();
            setMinimumSize(calculateMinimumSizeOf(width, height));
        }
    }, []);

    useEffect(() => {

        if (!containerSize) {
            return;
        }

        setCrop({
            x: containerSize.width / imageSize.width * activeCropBox.x,
            y: containerSize.height / imageSize.height * activeCropBox.y,
            width: containerSize.width / imageSize.width * activeCropBox.width,
            height: containerSize.height / imageSize.height * activeCropBox.height,
            unit: 'px'
        } as PixelCrop);
    }, [activeCropBox, imageSize, setCrop, containerSize, aspectRatio]);

    const updateCropBox = useCallback((crop: Crop, cropRatio: Crop) => {

        setCrop(crop);

        onChange({
            x: Math.round(imageSize.width / 100 * cropRatio.x),
            y: Math.round(imageSize.height / 100 * cropRatio.y),
            width: Math.round(imageSize.width / 100 * cropRatio.width),
            height: Math.round(imageSize.height / 100 * cropRatio.height)
        });
    }, [imageSize, onChange]);

    const classes = classNames('ReactCrop__container', {
        [styles.animate]: animateControls
    });

    if (!crop) {
        return null;
    }

    return (
        <ReactCrop
            className={classes}
            crop={crop}
            aspect={aspectRatio}
            ruleOfThirds
            keepSelection
            onDragStart={() => setAnimateControls(false)}
            onChange={setCrop}
            minHeight={minimumSize}
            minWidth={minimumSize}
            onComplete={updateCropBox}
            renderSelectionAddon={() => (
                <CropOverlay
                    filter={filter}
                    url={overlayUrl}
                    crop={crop!} clip={clip}
                    containerSize={containerSize} />
            )}>
            <div ref={containerRef} className={styles.children}>
                {children}
            </div>
        </ReactCrop>
    );
};

const calculateMinimumSizeOf = (width: number, height: number) => {
    return Math.min(width, height) * MIN_CROP_BOX_SIZE_RATIO;
};

type Props = PropsWithChildren<{
    overlayUrl: string;
    onChange: (cropBox: Area) => void;
    imageSize: Size;
    activeCropBox: Area;
    containerSize: Size | undefined;
    clip: Clip | undefined;
    aspectRatio?: number;
    filter: string | null;
    hasPhotoFunBorder?: boolean;
}>;